import { Reveal, SectionHeading, EstimateButton, CallButton } from "@/components/Shared";
import { Award, MessageSquare, HeartHandshake } from "lucide-react";
import { IMAGES } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

const VALUES = [
  { icon: Award, title: "Quality workmanship", text: "Skilled tradespeople, quality materials, and finishes built to last — not just to look good on day one." },
  { icon: MessageSquare, title: "Clear communication", text: "You always know what's happening, what's next, and what it costs. No chasing, no guessing." },
  { icon: HeartHandshake, title: "Customer-focused service", text: "Your home is treated with respect: protected floors, a clean site every night, and a final walkthrough together." },
];

const STEPS = ["Free consultation", "Written fixed-price quote", "Professional build", "Final walkthrough"];

export default function About() {
  useSEO(
    "About MTC Home Renovation | GTA Renovation Company",
    "MTC Home Renovation is a professional renovation company serving the Greater Toronto Area — quality workmanship, transparent estimates and reliable scheduling on every project."
  );

  return (
    <>
      <section className="py-16 sm:py-24 border-b border-obsidian/10" data-testid="about-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-stonemuted">About Us · Greater Toronto Area</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-obsidian">
              A renovation company built on <em className="italic text-teak">workmanship.</em>
            </h1>
            <p className="mt-6 text-lg text-stone-600 font-light leading-relaxed">
              MTC Home Renovation is a professional renovation company serving homeowners across the GTA. We specialize in kitchens, bathrooms, basements, flooring and complete home transformations.
            </p>
            <p className="mt-4 text-stone-600 leading-relaxed">
              What sets us apart is simple: transparent estimates, a professional process, reliable scheduling, and a crew that treats your home like its own. Licensed, insured, and committed to getting the details right.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <EstimateButton testId="about-estimate-button" />
              <CallButton testId="about-call-button" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden border border-obsidian/15 aspect-[4/3]">
              <img src={IMAGES.C2} alt="MTC Home Renovation craftsman performing detailed renovation work in the GTA" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 border-b border-obsidian/10 bg-parchment/60" data-testid="about-values">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="How We Work" title="What you can expect from us." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-10">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <v.icon size={22} className="text-teak" />
                <h3 className="mt-4 font-display text-xl font-medium text-obsidian">{v.title}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 border-t border-obsidian/10 pt-8 flex flex-wrap items-center gap-x-3 gap-y-2">
            {STEPS.map((s, i) => (
              <span key={s} className="flex items-center gap-3 text-sm text-stone-700">
                <span className="font-mono text-xs text-teak">{String(i + 1).padStart(2, "0")}</span>
                {s}
                {i < STEPS.length - 1 && <span className="text-stonemuted mx-1">→</span>}
              </span>
            ))}
          </Reveal>
        </div>
      </section>
    </>
  );
}
